// Dungeon generator core (B1, monorail-fgy). Three-stage pipeline, each
// stage a pure seeded function so every layer is testable and visualizable
// on its own (research/roguelike-mapgen.md — "build the debug view first"):
//
//   grammar.ts  seed → DunGraph        (cyclic mission graph — the INTENT)
//   embed.ts    graph → positions      (force-directed, continuous space)
//   carve.ts    positions → SDF prims  (metaball rooms + capsule halls)
//
// The graph layer is geometry-free on purpose: cycle types carry the
// designed-not-random feel (Unexplored's insight), and geometry is just a
// rendering of that structure.

export interface Vec2 {
  x: number
  y: number
}

export type CycleType = 'two-routes' | 'lock-and-key' | 'valve-shortcut'

export const CYCLE_TYPES: readonly CycleType[] = ['two-routes', 'lock-and-key', 'valve-shortcut']

export type NodeRole =
  | 'entrance' // where the player comes in; carved extra-open
  | 'goal'     // far end of the cycle (boss/exit candidate — B2+ decides)
  | 'room'     // plain traversal room
  | 'key'      // holds the key for the lock edge (lock-and-key cycles)
  | 'reward'   // gated vault pocket — B4 seals it behind a door
  | 'treasure' // ungated treasure pocket (B6) — open hall, loot visible inside

/** Dead-end pockets hang off the web by one hall — both vaults and treasure. */
export function isPocketRole(role: NodeRole): boolean {
  return role === 'reward' || role === 'treasure'
}

/** Which route of the cycle a node sits on. */
export type ArcTag =
  | 'A'    // the long arc (entrance → goal the scenic way)
  | 'B'    // the short arc (blocked, gated, or valved — the interesting one)
  | 'side' // off-cycle: nested sub-loop nodes and dead-end pockets

export interface DunNode {
  id: number
  role: NodeRole
  arc: ArcTag
  /** Room footprint scale (1 = typical). carve multiplies into disc radii. */
  sizeHint: number
  /** World-region generator (B2): depth band index, 0 = hub. */
  band?: number
}

export type EdgeKind =
  | 'hall' // plain swimmable corridor
  | 'lock' // impassable until the key node is visited (gating lands in B4)
  | 'valve' // one-way pore: passable only b→a (squeeze back toward `a`)
  | 'throat' // THE descent passage between two regions (B3) — long, narrow, singular
  | 'shortcut' // Souls shortcut: carved but plugged by a door until opened from the far side

export interface DunEdge {
  a: number
  b: number
  kind: EdgeKind
  /** Hall width multiplier (B2 gradient: porous throats → corridors). */
  widthMult?: number
  /** Multiplies the open-hall portion of the embed rest length (throats). */
  restMult?: number
}

/** What a sealed plug answers to (B3 shortcuts, B4 gates). */
export type DoorKind =
  | 'shortcut'   // opens on contact from the far side (nx,ny points there)
  | 'adaptation' // opens on contact if the player owns the keyed power
  | 'lock'       // opens on contact if the player carries the key item

/** A door sealing a hall. Closed doors are walls (CPU sdf + shader both
 *  plug the hall); every kind opens PERMANENTLY (persisted per save). */
export interface DoorPlug {
  /** Edge index this door seals (stable per seed — the persistence key). */
  edge: number
  kind: DoorKind
  /** adaptation: a PowerId; lock: a key-item id (e.g. 'hall-pass'). */
  keyId?: string
  x: number
  y: number
  r: number
  /** shortcut: unit normal toward the opening side. Other kinds ignore it. */
  nx: number
  ny: number
  open: boolean
}

export interface DunGraph {
  seed: number
  cycleType: CycleType
  nodes: DunNode[]
  edges: DunEdge[]
  entrance: number
  goal: number
  /** Node ids along each arc, entrance→goal order, endpoints excluded. */
  arcA: number[]
  arcB: number[]
  /** lock-and-key only: node holding the key / index of the locked edge. */
  keyNode?: number
  lockEdge?: number
  /** valve-shortcut only: index of the one-way edge. */
  valveEdge?: number
}

// ── Carved geometry ─────────────────────────────────────────────────────────
// Water = smooth-union of discs + capsules; sdf < 0 is swimmable, > 0 is
// wall. Everything outside the carved web is solid (decision #1: the whole
// world becomes rooms + halls; B1 activates this only behind a cheat).

export interface WallDisc {
  x: number
  y: number
  r: number
  /** Node this disc belongs to (viz + later per-room logic). */
  node: number
}

export interface WallCapsule {
  ax: number
  ay: number
  bx: number
  by: number
  r: number
  /** Edge index this capsule carves. */
  edge: number
}

export interface DungeonLayout {
  graph: DunGraph
  /** Embedded node positions, indexed by node id (world space). */
  pos: Vec2[]
  discs: WallDisc[]
  capsules: WallCapsule[]
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
  // ── World-region extras (B2) — absent on plain B1 mission dungeons ──
  /** Per-node depth in virtual world units, rescaled onto the biome ladder
   *  (graph distance from the hub — replaces radial distance-from-origin). */
  nodeDepth?: number[]
  /** Nodes hosting the three navigation landmarks (reticle/droplet/duck). */
  landmarkNodes?: number[]
  /** The hub basin node (world origin; Mother Cell lives here). */
  hubNode?: number
  /** Shortcut doors (B3) — mutated in place when opened; render/collision
   *  read `open` every frame. */
  doors?: DoorPlug[]
}
